/**
 * Order ID Generator
 * Generates invoice-style order IDs: INV-YYYYMMDD-XXXX
 * Format: INV-20251120-0001
 * 
 * Uses timestamp-based sequence to ensure uniqueness across clients
 */

/**
 * Generate a new invoice-style order ID
 * Format: INV-YYYYMMDD-XXXX (e.g., INV-20251120-0001)
 * 
 * The sequence (XXXX) is derived from the timestamp to ensure uniqueness.
 * Uses the last 4 digits of the timestamp's milliseconds component.
 */
export function generateOrderId(): string {
  const now = new Date();
  
  // Use local date instead of UTC to match the displayed date on receipts
  // Format: YYYYMMDD using local timezone
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const dateStr = `${year}${month}${day}`;
  
  // Use the last 4 digits of the timestamp to create a unique sequence
  // This ensures high uniqueness while keeping the format readable
  const timestamp = now.getTime();
  const sequence = (timestamp % 10000).toString().padStart(4, '0');
  
  return `INV-${dateStr}-${sequence}`;
}

/**
 * Parse an order ID to extract date and sequence
 * Returns null if the format is invalid
 */
export function parseOrderId(orderId: string): { date: string; sequence: number } | null {
  const match = orderId.match(/^INV-(\d{8})-(\d{4})$/);
  if (!match) {
    return null;
  }
  
  return {
    date: match[1], // YYYYMMDD
    sequence: parseInt(match[2], 10),
  };
}

